"use client"

import { FC } from "react"
import { Content } from "@prismicio/client"
import { PrismicRichText, SliceComponentProps } from "@prismicio/react"
import { PrismicNextImage } from "@prismicio/next"
import { LazyMotion, domAnimation, m, type Variants } from "motion/react"
import { vFadeUp, vStaggerContainer } from "@/lib/animations"

export type HighlightedFeatureGridProps =
  SliceComponentProps<Content.HighlightedFeatureGridSlice>

const iconColorCycle = [
  "bg-gradient-fire",
  "bg-gradient-amber",
  "bg-gradient-blue",
  "bg-gradient-purple",
  "bg-gradient-green",
  "bg-gradient-crimson",
]

const textColorCycle = [
  "text-gradient-fire",
  "text-gradient-amber",
  "text-gradient-blue",
  "text-gradient-purple",
  "text-gradient-green",
  "text-gradient-crimson",
]

const vCardPop: Variants = {
  hidden: { opacity: 0, y: 36, scale: 0.95 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.55,
      ease: [0.34, 1.56, 0.64, 1],
      delay: i * 0.09,
    },
  }),
}

const vIconBounce: Variants = {
  hidden: { opacity: 0, scale: 0, rotate: -20 },
  visible: (i: number) => ({
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: {
      duration: 0.5,
      ease: [0.34, 1.56, 0.64, 1],
      delay: i * 0.09 + 0.2,
    },
  }),
}

const RING_ORNAMENTS = [
  { top: "4%", left: "1%", size: 48, delay: 0, opacity: 0.3 },
  { top: "20%", right: "2%", size: 30, delay: 0.2, opacity: 0.22 },
  { bottom: "12%", left: "5%", size: 24, delay: 0.45, opacity: 0.18 },
  { bottom: "6%", right: "6%", size: 40, delay: 0.65, opacity: 0.25 },
  { top: "55%", left: "0%", size: 18, delay: 0.35, opacity: 0.16 },
  { top: "38%", right: "1%", size: 22, delay: 0.55, opacity: 0.2 },
] as const

const HighlightedFeatureGrid: FC<HighlightedFeatureGridProps> = ({ slice }) => {
  const { primary } = slice

  return (
    <LazyMotion features={domAnimation}>
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        aria-label={slice.slice_type
          .split("_")
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")}
        className="relative overflow-hidden bg-white section-py"
      >
        {/* ── Ambient glow blobs ── */}
        <m.div
          aria-hidden
          className="pointer-events-none absolute -top-32 -left-32 size-112 rounded-full bg-sacred-orange/8 blur-3xl"
          initial={{ opacity: 0, scale: 0.75 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, ease: "easeOut" }}
          viewport={{ once: true }}
        />
        <m.div
          aria-hidden
          className="pointer-events-none absolute -right-32 -bottom-32 size-96 rounded-full bg-sacred-red/6 blur-3xl"
          initial={{ opacity: 0, scale: 0.75 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, ease: "easeOut", delay: 0.3 }}
          viewport={{ once: true }}
        />

        {/* ── Dot-grid texture ── */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle, oklch(0.65 0.14 60 / 0.15) 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />

        {/* ── Floating ring ornaments ── */}
        {RING_ORNAMENTS.map((ring, i) => (
          <m.div
            key={i}
            aria-hidden
            className="pointer-events-none absolute rounded-full border border-sacred-orange"
            style={{
              width: ring.size,
              height: ring.size,
              opacity: ring.opacity,
              top: "top" in ring ? ring.top : undefined,
              bottom: "bottom" in ring ? ring.bottom : undefined,
              left: "left" in ring ? ring.left : undefined,
              right: "right" in ring ? ring.right : undefined,
            }}
            initial={{ opacity: 0, scale: 0, rotate: -45 }}
            whileInView={{ opacity: ring.opacity, scale: 1, rotate: 0 }}
            transition={{
              duration: 0.8,
              delay: ring.delay,
              ease: [0.34, 1.56, 0.64, 1],
            }}
            viewport={{ once: true }}
          />
        ))}

        <div className="relative container flex flex-col gap-6 sm:gap-7 md:gap-8 lg:gap-9 xl:gap-10">
          {/* ── Section title ── */}
          {primary.section_title && (
            <m.div
              className="mx-auto max-w-2xl text-center"
              variants={vStaggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
            >
              <m.div variants={vFadeUp}>
                <PrismicRichText
                  field={primary.section_title}
                  components={{
                    heading2: ({ children }) => <h2>{children}</h2>,
                    paragraph: ({ children }) => (
                      <p className="text-muted-foreground">{children}</p>
                    ),
                  }}
                />
              </m.div>
            </m.div>
          )}

          {/* ── Feature grid ── */}
          {primary.features && primary.features.length > 0 && (
            <m.div
              className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
            >
              {primary.features.map((feature, i) => (
                <m.div
                  key={i}
                  custom={i}
                  variants={vCardPop}
                  className="flex flex-col gap-4 rounded-2xl border border-orange-100 bg-orange-50/60 p-6 shadow-sm transition-shadow hover:shadow-md"
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                >
                  {/* Icon */}
                  {feature.icon?.url && (
                    <m.div
                      custom={i}
                      variants={vIconBounce}
                      className={`flex size-12 items-center justify-center rounded-xl ${iconColorCycle[i % iconColorCycle.length]}`}
                    >
                      <PrismicNextImage
                        field={feature.icon}
                        fallbackAlt=""
                        width={32}
                        height={32}
                        className="size-8 object-contain"
                        imgixParams={{ auto: ["format", "compress"], q: 75 }}
                      />
                    </m.div>
                  )}

                  {/* Title */}
                  {feature.title && (
                    <h3
                      className={`font-bold ${textColorCycle[i % textColorCycle.length]}`}
                    >
                      {feature.title}
                    </h3>
                  )}

                  {/* Subtitle */}
                  {feature.subtitle && (
                    <p className="font-semibold text-gray-700">
                      {feature.subtitle}
                    </p>
                  )}

                  {/* Description */}
                  {feature.description && (
                    <PrismicRichText
                      field={feature.description}
                      components={{
                        paragraph: ({ children }) => (
                          <p className="text-gray-700">{children}</p>
                        ),
                      }}
                    />
                  )}
                </m.div>
              ))}
            </m.div>
          )}
        </div>
      </section>
    </LazyMotion>
  )
}

export default HighlightedFeatureGrid
